import * as React from 'react'
import { ArrowPathIcon } from '@heroicons/react/16/solid'
import { format } from 'date-fns'
import useAppStore from '@/store/useAppStore'
import useDashboardRefresh from '@/pages/dashboard/useDashboardRefresh'

/** "6 min ago" / "just now" — coarse on purpose, it's reassurance, not a clock. */
function agoLabel(from: number, now: number): string {
  const minutes = Math.floor((now - from) / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes === 1) return '1 min ago'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`
}

/**
 * Freshness readout plus a manual refetch.
 *
 * The dashboard already refetches on arrival, but a user who leaves it open
 * on a second monitor while editing elsewhere has no other way to know how
 * old the numbers are — hence the timestamp rather than a bare button.
 */
export default function DashboardRefreshControl() {
  const refreshedAt = useAppStore((state) => state.dashboardRefreshedAt)
  const refresh = useDashboardRefresh()
  const [pending, setPending] = React.useState(false)
  const [now, setNow] = React.useState(() => Date.now())

  // Re-tick the relative label while the page sits open.
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])

  const onRefresh = async () => {
    setPending(true)
    try {
      await refresh()
      setNow(Date.now())
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="flex items-center gap-2.5 whitespace-nowrap">
      {refreshedAt && (
        <span className="text-xs text-txt-secondary">
          Updated{' '}
          <span className="font-semibold text-txt-primary">
            {format(new Date(refreshedAt), 'h:mm a').toLowerCase()}
          </span>{' '}
          · {agoLabel(refreshedAt, now)}
        </span>
      )}
      <button
        type="button"
        onClick={onRefresh}
        disabled={pending}
        className="flex items-center gap-1.5 rounded-lg border border-primary bg-white px-2.5 py-1.5 text-sm font-semibold text-txt-primary disabled:opacity-60"
      >
        <ArrowPathIcon className={`size-3.5 ${pending ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>
  )
}
